/**
 * PublicOnly — marketing/visitor-only route guard.
 *
 * Public pages (landing, marketing, catalog entry, auth screens) are for
 * visitors. A signed-in user landing on one is sent to the equivalent
 * authenticated surface instead, so they never see public chrome.
 *
 * Deep links must not flash: before the auth context resolves we read the
 * persisted session synchronously, so an authenticated visitor redirects on
 * the very first render instead of painting the public page and then jumping.
 * When there is no stored session we render the public page immediately, so
 * genuine visitors never see a spinner either.
 *
 * Auth-only content pages (module reader, pathway detail, legal, credential
 * verification) are intentionally NOT wrapped: they serve both audiences.
 */

import { ReactNode, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { resolvePostAuthTarget } from "@/lib/authRedirect";
import { hasStoredSession } from "@/lib/auth/sessionHint";

interface PublicOnlyProps {
  children: ReactNode;
  /** Authenticated destination for this public page. */
  redirectTo?: string;
}

export function PublicOnly({ children, redirectTo = "/app/home" }: PublicOnlyProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  // Captured once per mount: avoids re-reading storage on every render and
  // keeps the first paint decision stable.
  const storedSession = useRef(hasStoredSession()).current;

  const params = new URLSearchParams(location.search);
  const target = resolvePostAuthTarget(params.get("redirect"), redirectTo);
  // Preserve catalog filters/goals when bouncing /learn?goal=… → /app/browse.
  // Skip when the target already has a query (e.g. explicit ?redirect=).
  const withSearch =
    !target.includes("?") && location.search && !params.get("redirect")
      ? `${target}${location.search}`
      : target;

  // Resolved: signed in -> straight into the app shell.
  if (!isLoading && isAuthenticated) {
    return <Navigate to={withSearch} replace />;
  }

  // Still resolving: trust the persisted session so the redirect happens on the
  // first frame. No spinner, no public-chrome flash, no layout jump.
  const isSignInRoute = location.pathname === "/auth" || location.pathname === "/login";
  // A stored hint never prevents reaching the existing sign-in form while the
  // real auth state is unresolved. Resolved signed-in routing above still wins.
  if (isLoading && storedSession && !isSignInRoute) {
    return <Navigate to={withSearch} replace />;
  }

  return <>{children}</>;
}
